import React, { useState, useCallback, useMemo, Suspense } from "react";
import { Navigate, useRoutes, BrowserRouter } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { useApplyBetaFeatureMask } from "./hooks/useBetaFeatures";
import ResponsiveLayout from "./components/ResponsiveLayout";
import {
  PageSkeleton,
  PageTransitionOutlet,
} from "./components/layout/PageTransition";
import ProtectedRoute from "./components/ProtectedRoute";
import TenantWorkspaceGuard from "./components/TenantWorkspaceGuard";
import { useStoryWorkspace, Workspace } from "./context/StoryWorkspaceContext";
import WorkspacePopup from "./popups/WorkspacePopup";
import { VideoProvider } from "./context/VideoContext";
import { StoryWorkspaceProvider } from "./context/StoryWorkspaceContext";
import { LayoutProvider } from "./context/LayoutContext";
import { TableauProvider } from "./contexts/TableauProvider";
import { useFeatureFlag } from "./utils/featureFlags";
import { buildAuthRoutes, withAuthShell } from "./routes/authRoutes";
import { ThemeProvider } from "./providers/ThemeProvider";
import { AppQueryProvider } from "./providers/QueryProvider";
import { FeedbackProvider } from "./contexts/FeedbackContext";
import { IntercomProvider } from "./providers/IntercomProvider";
import { StepperProvider } from "./contexts/StepperContext";
import InteractPage from "./pages/InteractPage";
import { buildDistributionRoutes } from "./pages/PersonalizedDistribution/distributionRoutes";
import { ToastViewport } from "./components/organisms/ToastViewport";
import { CommandRegistryProvider } from "./contexts/CommandRegistryProvider";
import { AssistantPanelProvider } from "./contexts/AssistantPanelContext";
import { ScreenAssistantPanel } from "./components/organisms/ScreenAssistantPanel";
import { CommandPaletteHost } from "./components/organisms/CommandPaletteHost";
import { VoiceInputButton } from "./components/molecules/VoiceInputButton";
import { buildBrandKitRoutes } from "./pages/BrandKits/brandKitRoutes";
import { buildDiscoverRoutes } from "./pages/Discover/discoverRoutes";
import { buildActivityRoutes } from "./pages/Activity/activityRoutes";
import { buildTemplatesRoutes } from "./pages/Templates/templatesRoutes";
import { LocationErrorBoundary } from "./components/organisms/RouteErrorBoundary";
import { ArchitectureGate } from "./components/organisms/ArchitectureGate";
import ToConnectorPage from "./pages/Connectors/v2/ToConnectorPage";

// Lazy load components
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const LoginMagicPage = React.lazy(() => import("./pages/LoginMagicPage"));
const SignupPage = React.lazy(() => import("./pages/SignupPage"));
const VerifyEmailPage = React.lazy(() => import("./pages/VerifyEmailPage"));
const Home = React.lazy(() => import("./pages/Home"));
const HomeNew = React.lazy(() => import("./pages/HomeNew"));
const HomeV2 = React.lazy(() => import("./pages/HomeV2"));
const AgentsPage = React.lazy(() => import("./pages/Agents"));
const AgentEditorPage = React.lazy(
  () => import("./pages/Agents/AgentEditorPage"),
);
const DataStoriesPage = React.lazy(() => import("./pages/DataStories"));
const DataStudioPage = React.lazy(() => import("./pages/DataStudio"));
const ConnectorsMarketplace = React.lazy(
  () => import("./pages/Connectors/Marketplace"),
);
const ConnectionDetail = React.lazy(
  () => import("./pages/Connectors/ConnectionDetail"),
);
const ConnectorsPageV2 = React.lazy(
  () => import("./pages/Connectors/v2/ConnectorsPageV2"),
);
const ConnectorDetailPageV2 = React.lazy(
  () => import("./pages/Connectors/v2/ConnectorDetailPageV2"),
);
const ConnectionDatasetPage = React.lazy(
  () => import("./pages/Connectors/ConnectionDatasetPage"),
);
const KgSectionLayout = React.lazy(
  () => import("./pages/KnowledgeGraph/KgSectionLayout"),
);
const KgPage = React.lazy(() => import("./pages/KnowledgeGraph/KgPage"));
const DataModelsPage = React.lazy(
  () => import("./pages/KnowledgeGraph/DataModelsPage"),
);
const AddDataModelPage = React.lazy(
  () => import("./pages/KnowledgeGraph/AddDataModelPage"),
);
const KgSectionPlaceholder = React.lazy(
  () => import("./pages/KnowledgeGraph/KgSectionPlaceholder"),
);
const KgDataStoriesPage = React.lazy(
  () => import("./pages/KnowledgeGraph/KgDataStoriesPage"),
);
const ReportsPage = React.lazy(
  () => import("./pages/KnowledgeGraph/ReportsPage"),
);
const DataSourcesPage = React.lazy(
  () => import("./pages/KnowledgeGraph/DataSourcesPage"),
);
const TablesPage = React.lazy(
  () => import("./pages/KnowledgeGraph/TablesPage"),
);
const MetricsPage = React.lazy(
  () => import("./pages/KnowledgeGraph/MetricsPage"),
);
const BusinessRulesPage = React.lazy(
  () => import("./pages/KnowledgeGraph/BusinessRulesPage"),
);
const TribalKnowledgePage = React.lazy(
  () => import("./pages/KnowledgeGraph/TribalKnowledgePage"),
);
const OntologiesPage = React.lazy(
  () => import("./pages/KnowledgeGraph/OntologiesPage"),
);
const OntologyDetailPage = React.lazy(
  () => import("./pages/KnowledgeGraph/OntologyDetailPage"),
);
const Automation = React.lazy(() => import("./pages/Automation"));
const Explore = React.lazy(() => import("./pages/Explore"));
const Build = React.lazy(() => import("./pages/Build"));
const ExploreMapPage = React.lazy(() => import("./pages/Story/ExploreMapPage"));
const StoryExplorePage = React.lazy(() => import("./pages/Story/StoryExplorePage"));
// const ExperiencePage = React.lazy(() => import("./pages/ExperiencePage"));
const Settings = React.lazy(() => import("./pages/Settings"));
const AdminSignupsPage = React.lazy(() => import("./pages/AdminSignupsPage"));
const DesignSystem = React.lazy(() => import("./pages/DesignSystem"));
const DataAgentsDemoPage = React.lazy(
  () => import("./pages/DataAgentsDemoPage"),
);
const WorkspacesListPage = React.lazy(
  () => import("./pages/Workspaces/WorkspacesListPage"),
);
const WorkspaceDetailPage = React.lazy(
  () => import("./pages/Workspaces/WorkspaceDetailPage"),
);
const ForgotPasswordPage = React.lazy(
  () => import("./pages/ForgotPasswordPage"),
);
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const OAuthCallbackPage = React.lazy(() => import("./pages/OAuthCallbackPage"));
const SelectTenantPage = React.lazy(() => import("./pages/SelectTenantPage"));
const EmbedStoryPage = React.lazy(() => import("./pages/EmbedStoryPage"));
const StoryDetailPage = React.lazy(
  () => import("./pages/Story/StoryDetailPage"),
);
const StoryGeneratePage = React.lazy(
  () => import("./pages/Story/StoryGeneratePage"),
);

// New workspace-scoped story layout routes
const WorkspaceLayout = React.lazy(
  () => import("./pages/WorkspaceStory/WorkspaceLayout"),
);
const StoryLayout = React.lazy(
  () => import("./pages/WorkspaceStory/StoryLayout"),
);
const StoryPlanPage = React.lazy(() => import("./pages/Story/StoryDetailPage"));
const StoryBuildPage = React.lazy(
  () => import("./pages/Story/StoryGeneratePage"),
);
const StoryPresentPage = React.lazy(
  () => import("./pages/Story/StoryPresentPage"),
);
const CsvLoadingPage = React.lazy(() => import("./pages/Story/CsvLoadingPage"));

const ProtectedContent = React.memo(() => {
  const { logout } = useAuth();
  // Applies the organization's beta allow-list to personal beta overrides, once for the signed-in app.
  useApplyBetaFeatureMask();
  const [showWorkspacePopup, setShowWorkspacePopup] = useState(false);
  const { setWorkspaces, setWorkspace } = useStoryWorkspace();

  const handleWorkspaceCreated = useCallback(
    (newWorkspace: Workspace) => {
      setWorkspaces([newWorkspace]);
      setWorkspace(newWorkspace);
      setShowWorkspacePopup(false);
    },
    [setWorkspaces, setWorkspace],
  );

  const memoizedLogout = useCallback(logout, [logout]);

  return (
    <ResponsiveLayout handleLogout={memoizedLogout}>
      <>
        {showWorkspacePopup && (
          <WorkspacePopup
            onSuccess={handleWorkspaceCreated}
            onClose={() => setShowWorkspacePopup(false)}
          />
        )}
        <PageTransitionOutlet scope="section" />
      </>
    </ResponsiveLayout>
  );
});

const App: React.FC = React.memo(() => {
  const { isLoggedIn, loading, login } = useAuth();
  // const isExperiencePageEnabled = useFeatureFlag('EXPERIENCE_PAGE');
  // const isInteractPageEnabled = useFeatureFlag('INTERACT_PAGE');
  const isDesignSystemEnabled = useFeatureFlag("DESIGN_SYSTEM_SHOWCASE");

  const routes = useMemo(
    () => [
      ...buildAuthRoutes({
        signIn: <LoginPage onLoginSuccess={login} />,
        signUp: <SignupPage onLoginSuccess={login} />,
        forgotPassword: <ForgotPasswordPage />,
        resetPassword: <ResetPasswordPage />,
        verifyEmail: <VerifyEmailPage />,
        oauthCallback: <OAuthCallbackPage onLoginSuccess={login} />,
        selectOrganization: (
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <SelectTenantPage />
          </ProtectedRoute>
        ),
        signInMagic: <LoginMagicPage onLoginSuccess={login} />,
      }),
      {
        path: "/landing",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/embed/:token",
        element: <EmbedStoryPage />,
      },
      { path: "/s/:slug", element: <EmbedStoryPage /> },
      { path: "/e/:slug", element: <EmbedStoryPage /> },
      // {
      //   path: "/experience",
      //   element: isExperiencePageEnabled ? <ExperiencePage /> : <Navigate to="/" replace />,
      // },
      // {
      //   path: "/interact",
      //   element: isInteractPageEnabled ? <InteractPage /> : <Navigate to="/" replace />,
      // },
      {
        path: "/design-system",
        element: isDesignSystemEnabled ? (
          <DesignSystem />
        ) : (
          <Navigate to="/" replace />
        ),
      },
      {
        path: "/",
        element: isLoggedIn ? (
          <Navigate to="/home" replace />
        ) : (
          withAuthShell(<LoginPage onLoginSuccess={login} />)
        ),
      },
      {
        element: (
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <TenantWorkspaceGuard>
              <ProtectedContent />
            </TenantWorkspaceGuard>
          </ProtectedRoute>
        ),
        children: [
          { path: "/home-old", element: <Home /> },
          { path: "/home-legacy", element: <HomeNew /> },
          { path: "/home", element: <HomeV2 /> },
          { path: "/agents", element: <AgentsPage /> },
          { path: "/agents/create", element: <AgentEditorPage /> },
          { path: "/agents/:agentId/edit", element: <AgentEditorPage /> },
          { path: "/interact", element: <InteractPage /> },
          { path: "/stories", element: <DataStoriesPage /> },
          {
            path: "/data-studio",
            element: (
              <ArchitectureGate
                old={<DataStudioPage />}
                current={<DataStudioPage />}
              />
            ),
          },
          // { path: "/data-connectors", element: <DataConnectors /> },
          {
            path: "/data-connectors",
            element: (
              <ArchitectureGate
                old={<ConnectorsMarketplace />}
                current={<ConnectorsPageV2 />}
              />
            ),
          },
          {
            path: "/data-connectors/:connectionId/tables/:schema/:tableName",
            element: (
              <ArchitectureGate
                old={<ConnectionDatasetPage />}
                current={<ToConnectorPage />}
              />
            ),
          },
          {
            path: "/data-connectors/:connectionId/data-studio",
            element: (
              <ArchitectureGate
                old={<DataStudioPage />}
                current={<DataStudioPage />}
              />
            ),
          },
          {
            path: "/data-connectors/:connectionId",
            element: (
              <ArchitectureGate
                old={<ConnectionDetail />}
                current={<ConnectorDetailPageV2 />}
              />
            ),
          },
          { path: "/automation", element: <Automation /> },
          {
            path: "/knowledge-graph",
            element: <KgSectionLayout />,
            children: [
              { index: true, element: <Navigate to="canvas" replace /> },
              { path: "canvas", element: <KgPage /> },
              { path: "data-models", element: <DataModelsPage /> },
              { path: "data-models/new", element: <AddDataModelPage /> },
              {
                path: "data-models/:dataModelId",
                element: <AddDataModelPage />,
              },
              {
                path: "data-sources",
                element: (
                  <ArchitectureGate
                    old={<DataSourcesPage />}
                    current={<Navigate to="/data-connectors" replace />}
                  />
                ),
              },
              { path: "tables", element: <TablesPage /> },
              { path: "ontologies", element: <OntologiesPage /> },
              { path: "ontologies/:id", element: <OntologyDetailPage /> },
              { path: "metrics", element: <MetricsPage /> },
              { path: "business-rules", element: <BusinessRulesPage /> },
              { path: "tribal-knowledge", element: <TribalKnowledgePage /> },
              { path: "reports", element: <ReportsPage /> },
              { path: "data-stories", element: <KgDataStoriesPage /> },
              { path: "platform-history", element: <KgSectionPlaceholder /> },
              { path: "feedback", element: <KgSectionPlaceholder /> },
              { path: "artifacts", element: <KgSectionPlaceholder /> },
            ],
          },
          { path: "/workspaces", element: <WorkspacesListPage /> },
          { path: "/workspaces/:id", element: <WorkspaceDetailPage /> },

          // Workspace-scoped story routes
          {
            path: "/workspace/:workspaceId",
            element: <WorkspaceLayout />,
            children: [
              { path: "csv-loading", element: <CsvLoadingPage /> },
              {
                path: "story/:storyId",
                element: <StoryLayout />,
                children: [
                  { path: "plan/:storyPlanId", element: <StoryPlanPage /> },
                  { path: "build", element: <StoryBuildPage /> },
                  { path: "present", element: <StoryPresentPage /> },
                  { path: "explore", element: <StoryExplorePage /> },
                  ...buildDistributionRoutes(),
                ],
              },
            ],
          },

          // TODO: remove once all entry points navigate to /workspace/:workspaceId/story/:storyId/*
          // { path: "/story/detail/:storyId", element: <StoryDetailPage /> },
          // { path: "/story/generate/:storyId", element: <StoryGeneratePage /> },
          // { path: "/explore/:workspaceId/:storyId", element: <Explore /> },
          // { path: "/build/:workspaceId/:storyId", element: <Build /> },
          { path: "/explore", element: <ExploreMapPage /> },
          { path: "/settings", element: <Settings /> },
          { path: "/settings/:group/:section", element: <Settings /> },
          ...buildBrandKitRoutes(),
          ...buildDiscoverRoutes(),
          ...buildActivityRoutes(),
          ...buildTemplatesRoutes(),
          { path: "/admin/signups", element: <AdminSignupsPage /> },
          { path: "/dev/data-agents", element: <DataAgentsDemoPage /> },
        ],
      },
      {
        path: "*",
        element: <Navigate to={isLoggedIn ? "/home" : "/login"} replace />,
      },
    ],
    [
      isLoggedIn,
      login,
      isDesignSystemEnabled,
    ],
  );

  const routeElement = useRoutes(routes);

  if (loading) {
    return <PageSkeleton />;
  }

  return (
    <>
      {/* A render error or a stale lazy chunk shows a recoverable state, never a blank page. */}
      <LocationErrorBoundary>
        <Suspense fallback={<PageSkeleton />}>{routeElement}</Suspense>
      </LocationErrorBoundary>
    </>
  );
});

const AppWithProviders: React.FC = () => (
  <BrowserRouter>
    <ThemeProvider defaultMode="light" enableSystem={false}>
      <AppQueryProvider>
          <LayoutProvider>
            <StoryWorkspaceProvider>
                <VideoProvider>
                    <TableauProvider>
                      <StepperProvider>
                        <AssistantPanelProvider>
                        <CommandRegistryProvider>
                          <IntercomProvider>
                          <FeedbackProvider>
                          <App />
                          </FeedbackProvider>
                          </IntercomProvider>
                          {/* The command palette and shortcuts sheet, once for the app; the mic fills the palette's search. */}
                          <CommandPaletteHost searchEnd={(setQuery) => <VoiceInputButton onText={setQuery} size="sm" />} />
                          {/* The app's one notification stack: anything can call notify() (services/notify). */}
                          <ToastViewport />
                          {/* MagicBI panel for screens without their own; Mod+/ and the navbar icon toggle it. */}
                          <ScreenAssistantPanel />
                        </CommandRegistryProvider>
                        </AssistantPanelProvider>
                      </StepperProvider>
                    </TableauProvider>
                </VideoProvider>
            </StoryWorkspaceProvider>
          </LayoutProvider>
      </AppQueryProvider>
    </ThemeProvider>
  </BrowserRouter>
);

export default AppWithProviders;
